/**
 * Routering en toegangsbewaking van de frontend.
 *
 * Drie toestanden bepalen waar een gebruiker terechtkomt:
 *  - geen sessie            -> loginscherm (US-001)
 *  - sessie zonder bedrijf  -> bedrijfskeuze (US-003)
 *  - sessie met bedrijf     -> app-shell met de schermen die bij de rol horen (US-002/030)
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import { api } from './api/client.js';
import { startSentry } from './lib/sentry.js';
import { AppShell } from './layout/AppShell.js';
import { navItemForPath } from './layout/navigation.js';
import { CompanyPickerScreen } from './screens/CompanyPickerScreen.js';
import { ConfiguratorScreen } from './screens/ConfiguratorScreen.js';
import { HistoryScreen } from './screens/HistoryScreen.js';
import { OverviewScreen } from './screens/OverviewScreen.js';
import { ProductDetailScreen } from './screens/ProductDetailScreen.js';
import { PrijsgrafiekScreen } from './screens/PrijsgrafiekScreen.js';
import { ProductsScreen } from './screens/ProductsScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { VersiesScreen } from './screens/VersiesScreen.js';
import { AiUsageScreen } from './screens/AiUsageScreen.js';
import { TasksScreen } from './screens/TasksScreen.js';
import { ImportScreen } from './screens/ImportScreen.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { NoAccessScreen } from './screens/NoAccessScreen.js';
import { SessionProvider, useSession } from './session/SessionContext.js';

interface PublicConfig {
  environment: string;
  entraConfigured: boolean;
  devLoginEnabled: boolean;
  sentryDsn: string | null;
}

function FullPageMessage({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: 'var(--c-text-secondary)',
        fontSize: 'var(--fs-12-5)',
      }}
    >
      <span className="spinner" aria-hidden="true" />
      {text}
    </div>
  );
}

/** Laat alleen door wanneer er een sessie mét gekozen bedrijf is. */
function RequireCompany({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { status, session } = useSession();

  if (status === 'laden') return <FullPageMessage text="Bezig met laden…" />;
  if (status === 'uitgelogd') return <Navigate to="/" replace />;
  if (!session?.activeCompanyId) return <Navigate to="/bedrijf" replace />;

  return <>{children}</>;
}

/** Toont de "Geen toegang"-kaart wanneer een prijsanalist een beheerdersscherm opent. */
function RoleGuard({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isAdmin } = useSession();
  const location = useLocation();
  const item = navItemForPath(location.pathname);

  if (item?.adminOnly && !isAdmin) return <NoAccessScreen />;
  return <>{children}</>;
}

function EntryScreen({ config }: { config: PublicConfig | null }): React.JSX.Element {
  const { status, session } = useSession();

  if (status === 'laden') return <FullPageMessage text="Bezig met laden…" />;
  if (status === 'ingelogd') {
    return <Navigate to={session?.activeCompanyId ? '/prijsoverzicht' : '/bedrijf'} replace />;
  }
  return <LoginScreen devLoginAvailable={config?.devLoginEnabled ?? false} />;
}

function CompanyRoute(): React.JSX.Element {
  const { status, session } = useSession();

  if (status === 'laden') return <FullPageMessage text="Bezig met laden…" />;
  if (status === 'uitgelogd') return <Navigate to="/" replace />;
  // Bij precies één bedrijf heeft de server de keuze al gemaakt (US-003).
  if (session?.activeCompanyId) return <Navigate to="/prijsoverzicht" replace />;

  return <CompanyPickerScreen />;
}

function AppRoutes({ config }: { config: PublicConfig | null }): React.JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<EntryScreen config={config} />} />
      <Route path="/bedrijf" element={<CompanyRoute />} />

      <Route
        element={
          <RequireCompany>
            <AppShell />
          </RequireCompany>
        }
      >
        <Route path="/prijsoverzicht" element={<OverviewScreen />} />
        <Route path="/product/:id" element={<ProductDetailScreen />} />
        <Route path="/prijsgrafiek" element={<PrijsgrafiekScreen />} />
        <Route path="/prijshistorie" element={<HistoryScreen />} />
        <Route
          path="/producten"
          element={
            <RoleGuard>
              <ProductsScreen />
            </RoleGuard>
          }
        />
        <Route
          path="/bronconfigurator"
          element={
            <RoleGuard>
              <ConfiguratorScreen />
            </RoleGuard>
          }
        />
        <Route
          path="/import"
          element={
            <RoleGuard>
              <ImportScreen />
            </RoleGuard>
          }
        />
        <Route path="/taken" element={<TasksScreen />} />
        <Route
          path="/ai-verbruik"
          element={
            <RoleGuard>
              <AiUsageScreen />
            </RoleGuard>
          }
        />
        <Route
          path="/instellingen"
          element={
            <RoleGuard>
              <SettingsScreen />
            </RoleGuard>
          }
        />
        {/*
          De versiegeschiedenis staat bewust niet in de navigatie: je komt er via het
          versienummer linksonder, en dat is de plek waar de vraag ontstaat. Wél voor iedere rol
          leesbaar — er staat geen bedrijfsgegeven in.
        */}
        <Route path="/versies" element={<VersiesScreen />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App(): React.JSX.Element {
  const [config, setConfig] = useState<PublicConfig | null>(null);

  useEffect(() => {
    api
      .get<PublicConfig>('/api/config')
      .then((publiek) => {
        setConfig(publiek);
        void startSentry(publiek.sentryDsn, publiek.environment);
      })
      .catch(() => setConfig(null));
  }, []);

  return (
    <BrowserRouter>
      <SessionProvider>
        <AppRoutes config={config} />
      </SessionProvider>
    </BrowserRouter>
  );
}
